import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  adminListUsers, adminCreateUser, adminPatchUser, adminDeleteUser, formatApiErrorDetail,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Trash2, ShieldCheck, RefreshCw, LogOut, UserPlus, Eye, EyeOff,
} from "lucide-react";

const ROLE_STYLES = {
  admin:    "text-primary border-primary/40 bg-primary/10",
  employee: "text-foreground border-border/60 bg-secondary/60",
  dealer:   "text-foreground border-border/60 bg-secondary/60",
};

const EMPTY_FORM = { name: "", email: "", password: "", role: "employee" };

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | admin | employee | dealer

  // Create-user dialog
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await adminListUsers();
      setUsers(list);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function submitCreate(e) {
    e.preventDefault();
    setCreating(true); setCreateError(null);
    try {
      const created = await adminCreateUser(form);
      setUsers((cur) => [created, ...cur]);
      setForm(EMPTY_FORM);
      setOpenCreate(false);
      toast.success(`Account created for ${created.email}`);
    } catch (e) {
      setCreateError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  }

  async function patch(id, body, successMsg) {
    try {
      const u = await adminPatchUser(id, body);
      setUsers((cur) => cur.map((x) => (x.id === id ? u : x)));
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  async function remove(id, email) {
    try {
      await adminDeleteUser(id);
      setUsers((cur) => cur.filter((x) => x.id !== id));
      toast.success(`Removed ${email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const counts = {
    all: users.length,
    admin: users.filter((u) => u.role === "admin").length,
    employee: users.filter((u) => u.role === "employee").length,
    dealer: users.filter((u) => u.role === "dealer").length,
  };
  const shown = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/60 bg-card/60 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
          <button onClick={() => nav("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-[10px] uppercase tracking-widest">Dashboard</span>
          </button>
          <div className="flex-1" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hidden sm:inline">
            {user?.email}
          </span>
          <Button variant="ghost" size="sm" className="rounded-sm h-8" onClick={logout} data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" /> // admin
            </div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">User management</h1>
            <p className="text-sm text-muted-foreground mt-1">Create accounts and control who has access.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={reload} data-testid="admin-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) { setForm(EMPTY_FORM); setCreateError(null); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-sm h-8" data-testid="admin-create-open">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> New user
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-md">
                <DialogHeader>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// new user</div>
                  <DialogTitle>Create an account</DialogTitle>
                  <DialogDescription>
                    Set an initial password — the new user will be able to sign in immediately with these credentials.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitCreate} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="nu-name">Full name</Label>
                    <Input id="nu-name" data-testid="new-user-name" required
                      value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="nu-email">Email</Label>
                    <Input id="nu-email" data-testid="new-user-email" type="email" required
                      value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="nu-password">Initial password</Label>
                    <div className="relative">
                      <Input
                        id="nu-password" data-testid="new-user-password"
                        type={showPassword ? "text" : "password"}
                        minLength={6} required autoComplete="new-password"
                        value={form.password}
                        onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                        className="pr-9"
                      />
                      <button
                        type="button"
                        data-testid="new-user-password-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">Share this password with the user — they can change it later.</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm((s) => ({ ...s, role: v }))}>
                      <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="dealer">Dealer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {createError && (
                    <div data-testid="new-user-error" className="text-sm text-destructive font-mono border border-destructive/30 bg-destructive/10 rounded-sm px-3 py-2">
                      {createError}
                    </div>
                  )}
                  <DialogFooter className="pt-1">
                    <Button type="button" variant="outline" className="rounded-sm h-8 font-mono uppercase tracking-widest text-[11px]" onClick={() => setOpenCreate(false)}>Cancel</Button>
                    <Button data-testid="new-user-submit" type="submit" disabled={creating} className="rounded-sm h-8 font-mono uppercase tracking-widest text-[11px]">
                      {creating ? "Creating…" : "Create user"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Role filter tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-border/60">
          {["all", "admin", "employee", "dealer"].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${
                filter === f ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {f} <span className="ml-1 opacity-60">({counts[f]})</span>
            </button>
          ))}
        </div>

        {/* User rows */}
        <div className="border border-border/60 rounded-sm bg-card divide-y divide-border/40">
          {loading && (
            <div className="p-6 font-mono text-sm text-muted-foreground text-center">Loading…</div>
          )}
          {!loading && shown.length === 0 && (
            <div className="p-6 font-mono text-sm text-muted-foreground text-center">No users match this filter.</div>
          )}
          {shown.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="p-4 flex items-center gap-4 flex-wrap sm:flex-nowrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{u.name || u.email}</div>
                <div className="font-mono text-[11px] text-muted-foreground truncate">{u.email}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-widest ${ROLE_STYLES[u.role] || ROLE_STYLES.employee}`}>
                {u.role}
              </span>
              <div className="flex items-center gap-1.5">
                <Select value={u.role} disabled={u.id === user?.id} onValueChange={(v) => patch(u.id, { role: v }, "Role updated")}>
                  <SelectTrigger data-testid={`role-select-${u.id}`} className="h-7 w-28 text-[11px] rounded-sm disabled:opacity-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="dealer">Dealer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                {u.id !== user?.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-${u.id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-sm text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Their account and all of their projects will be deleted permanently. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`delete-confirm-${u.id}`} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(u.id, u.email)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground">← Back to dashboard</Link>
        </div>
      </main>
    </div>
  );
}
